import React, { useState, useEffect } from 'react'
import {
  useDataProvider,
  useNotify,
  Button,
  useRefresh,
  useRecordContext,
} from 'react-admin'
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { QuestionnaireType } from './OrganisationRiskQuestionnaireDirectory'
import VendorFormLayout from '../components/VendorFormLayout'

// Define the form section type
interface FormField {
  id: string
  label: string
  source: string
  required: boolean
  fieldType?: 'yesno' | 'dropdown'
  options?: string[] // For dropdown fields
}

interface FormSection {
  id: string
  title: string
  fields: FormField[]
}

// Field validation state
interface FieldValidation {
  error: boolean
  helperText: string
  showHelper: boolean
}

// Generate a unique ID
const generateUniqueId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`
}

interface FormPreviewProps {
  formSections: FormSection[]
  formName: string
  formDescription: string
}

// Preview form component
const FormPreview: React.FC<FormPreviewProps> = ({
  formSections,
  formName,
  formDescription,
}) => {
  return (
    <VendorFormLayout
      formSections={formSections}
      formName={formName}
      formDescription={formDescription}
      readOnly
    />
  )
}

interface OrganisationVendorFormProps {
  formId?: string
  isModal?: boolean
  onClose?: () => void
  customEndpoint?: (
    organisationId: number | undefined,
    formId: string | undefined
  ) => string
  formType?: string
  questionnaireType?: QuestionnaireType
}

const OrganisationVendorForm = ({
  formId,
  isModal = false,
  onClose,
  customEndpoint,
  formType = 'Vendor Form',
  questionnaireType,
}: OrganisationVendorFormProps) => {
  const dataProvider = useDataProvider()
  const notify = useNotify()
  const refresh = useRefresh()
  const record = useRecordContext()

  // State to store the form sections
  const [formSections, setFormSections] = useState<FormSection[]>([])
  const [originalData, setOriginalData] = useState<any>(null)
  const [formName, setFormName] = useState('')
  const [formDescription, setFormDescription] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  // State for field validation
  const [fieldValidations, setFieldValidations] = useState<{
    [key: string]: FieldValidation
  }>({})

  // Generate a unique field source based on section and field index
  const generateFieldSource = (
    sectionIndex: number,
    fieldIndex: number
  ): string => {
    return `vendor_form_section_${sectionIndex + 1}_field_${fieldIndex + 1}`
  }

  // Check if a field label is unique within its section
  const isFieldLabelUnique = (
    sectionIndex: number,
    fieldIndex: number,
    label: string
  ): boolean => {
    if (!label.trim()) return true // Empty labels are handled by a different validation

    const section = formSections[sectionIndex]
    return !section.fields.some(
      (field, idx) => idx !== fieldIndex && field.label.trim() === label.trim()
    )
  }

  // Validate a field label
  const validateFieldLabel = (
    sectionIndex: number,
    fieldIndex: number,
    label: string
  ): FieldValidation => {
    if (!label.trim()) {
      return {
        error: true,
        helperText: 'Field label is required',
        showHelper: true,
      }
    }

    if (!isFieldLabelUnique(sectionIndex, fieldIndex, label)) {
      return {
        error: true,
        helperText: 'Field label must be unique within this section',
        showHelper: true,
      }
    }

    return {
      error: false,
      helperText: '',
      showHelper: false,
    }
  }

  // Update field validation state
  const updateFieldValidation = (key: string, validation: FieldValidation) => {
    setFieldValidations((prev) => ({
      ...prev,
      [key]: validation,
    }))
  }

  // Load the organisation's form configuration
  useEffect(() => {
    const loadVendorForm = async () => {
      try {
        setLoading(true)
        const organisationId = record?.id

        // If formId is provided, load that specific form
        let endpoint
        if (customEndpoint) {
          endpoint = customEndpoint(
            organisationId ? Number(organisationId) : undefined,
            formId
          )
        } else if (formId) {
          endpoint = organisationId
            ? `vendor-forms/${formId}/organisations/${organisationId}`
            : `organisations/current/vendor-forms/${formId}`
        } else {
          endpoint = organisationId
            ? `vendor-forms/organisations/${organisationId}`
            : 'organisations/current/vendor-forms'
        }

        const response = await dataProvider.custom(endpoint, {
          method: 'GET',
        })

        // Store the original data for use in the update call
        setOriginalData(response.data)

        // Set form name and description
        if (response.data?.name) {
          setFormName(response.data.name)
        }
        if (response.data?.description) {
          setFormDescription(response.data.description)
        }

        if (response.data?.vendor_form) {
          // Ensure all sections and fields have unique IDs
          const fieldsWithIds = (section: FormSection) =>
            section.fields.map((field) => ({
              ...field,
              id: field.id ?? generateUniqueId(),
            }))
          const formWithIds = response.data.vendor_form.map(
            (section, sectionIndex) => {
              // If this is the first section, ensure the first field has source "name"
              if (sectionIndex === 0 && section.fields.length > 0) {
                const firstField = section.fields[0]
                let updated = false

                // Create an updated field with the necessary changes
                const updatedField = { ...firstField }

                // Ensure the label includes "name"
                if (!firstField.label.toLowerCase().includes('name')) {
                  updatedField.label = 'Vendor Name'
                  updated = true
                }

                // Ensure the source is "name"
                if (firstField.source !== 'name') {
                  updatedField.source = 'name'
                  updated = true
                }

                // Ensure the field is required
                if (!firstField.required) {
                  updatedField.required = true
                  updated = true
                }

                // Update the field if changes were made
                if (updated) {
                  section.fields[0] = updatedField
                  notify(
                    `The first field has been updated to be the vendor name field with source "name"`,
                    { type: 'info' }
                  )
                }
              }

              return {
                ...section,
                id: section.id ?? generateUniqueId(),
                fields: fieldsWithIds(section),
              }
            }
          )
          setFormSections(formWithIds)
        } else {
          // Initialise with an empty array if no configuration is found
          setFormSections([])

          // If originalData exists but doesn't have vendor_form, add it as empty array
          if (response.data) {
            setOriginalData({
              ...response.data,
              vendor_form: [],
            })
          }
        }
      } catch (error: unknown) {
        console.error(`Error loading ${formType.toLowerCase()}:`, error)
        notify(`Error loading ${formType.toLowerCase()} configuration`, {
          type: 'error',
        })
        // Initialize with an empty array on error
        setFormSections([])
        // Initialize originalData with an empty object
        setOriginalData({ vendor_form: [] })
      } finally {
        setLoading(false)
      }
    }

    loadVendorForm()
  }, [
    dataProvider,
    notify,
    setOriginalData,
    record,
    formId,
    customEndpoint,
    formType,
  ])

  // Check if all fields in a section have valid labels
  const areSectionFieldsValid = (sectionIndex: number): boolean => {
    const section = formSections[sectionIndex]
    return section.fields.every((field, fieldIndex) => {
      const validation = validateFieldLabel(
        sectionIndex,
        fieldIndex,
        field.label
      )
      return !validation.error
    })
  }

  // Validate a section title
  const validateSectionTitle = (
    sectionIndex: number,
    title: string
  ): FieldValidation => {
    if (!title.trim()) {
      return {
        error: true,
        helperText: 'Section title is required',
        showHelper: true,
      }
    }

    return {
      error: false,
      helperText: '',
      showHelper: false,
    }
  }

  // Check if all sections have valid titles
  const areSectionTitlesValid = (): boolean => {
    return formSections.every((section, sectionIndex) => {
      const validation = validateSectionTitle(sectionIndex, section.title)
      return !validation.error
    })
  }

  // Check if the first field in the first section has source "name"
  const hasNameTagCategory = (): boolean => {
    if (formSections.length === 0 || formSections[0].fields.length === 0) {
      return false
    }

    const firstField = formSections[0].fields[0]
    return firstField.source === 'name'
  }

  // Check if all sections have valid titles and fields
  const isFormValid = (): boolean => {
    return (
      formName.trim() !== '' &&
      hasNameTagCategory() &&
      areSectionTitlesValid() &&
      formSections.every((_, sectionIndex) => {
        return areSectionFieldsValid(sectionIndex)
      })
    )
  }

  // Save the form configuration
  const saveVendorForm = async () => {
    // Check if form name is provided
    if (!formName.trim()) {
      notify('Form name is required', { type: 'warning' })
      return
    }

    // Check if the first field has the "name" tag category
    if (!hasNameTagCategory()) {
      notify(
        'The first field in the first section must have a source of "name" to link to the tag category',
        { type: 'warning' }
      )
      return
    }

    // Validate the form before saving
    if (!isFormValid()) {
      notify('Please fix validation errors before saving', { type: 'warning' })

      // Show all validation errors
      const newValidations = { ...fieldValidations }

      // Validate section titles
      formSections.forEach((section, sectionIndex) => {
        const titleKey = `section-${sectionIndex}-title`
        const titleValidation = validateSectionTitle(
          sectionIndex,
          section.title
        )
        newValidations[titleKey] = {
          ...titleValidation,
          showHelper: true,
        }

        // Validate field labels
        section.fields.forEach((field, fieldIndex) => {
          const key = `${sectionIndex}-${fieldIndex}-label`
          const validation = validateFieldLabel(
            sectionIndex,
            fieldIndex,
            field.label
          )
          newValidations[key] = {
            ...validation,
            showHelper: true,
          }
        })
      })
      setFieldValidations(newValidations)

      return
    }

    try {
      setSaving(true)
      const organisationId = record?.id

      // Determine the endpoint based on whether we're editing a specific form
      let endpoint
      if (customEndpoint) {
        endpoint = customEndpoint(
          organisationId ? Number(organisationId) : undefined,
          formId
        )
      } else if (formId) {
        endpoint = organisationId
          ? `vendor-forms/${formId}/organisations/${organisationId}`
          : `organisations/current/vendor-forms/${formId}`
      } else {
        endpoint = organisationId
          ? `vendor-forms/organisations/${organisationId}`
          : 'organisations/current/vendor-forms'
      }

      await dataProvider.custom(endpoint, {
        method: 'PUT',
        body: JSON.stringify({
          vendor_form: formSections,
          name: formName || formType,
          description: formDescription || '',
        }),
      })

      notify(`${formType} configuration saved`, { type: 'success' })

      // If this is a modal, call the onClose callback
      if (isModal && onClose) {
        onClose()
      } else {
        refresh()
      }
    } catch (error: unknown) {
      console.error(`Error saving ${formType.toLowerCase()}:`, error)
      notify(`Error saving ${formType.toLowerCase()} configuration`, {
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  // Check if the maximum number of questions has been reached
  const hasReachedMaxQuestions = () => {
    if (!questionnaireType) return false

    // Count total fields across all sections
    const totalFields = formSections.reduce(
      (total, section) => total + section.fields.length,
      0
    )

    // Prequalification Questionnaires have a maximum of 10 questions
    if (
      questionnaireType === QuestionnaireType.PRELIMINARY &&
      totalFields >= 10
    ) {
      return true
    }

    // Risk Assessments have a maximum of 100 questions
    if (questionnaireType === QuestionnaireType.RISK && totalFields >= 100) {
      return true
    }

    return false
  }

  // Add a new section
  const addSection = () => {
    // Check if existing sections are valid before adding a new one
    if (formSections.length > 0 && !isFormValid()) {
      notify('Please fix validation errors before adding a new section', {
        type: 'warning',
      })
      return
    }

    // Check if maximum questions limit has been reached
    if (questionnaireType && hasReachedMaxQuestions()) {
      notify(
        `Maximum number of questions reached for ${
          questionnaireType === QuestionnaireType.PRELIMINARY
            ? 'Prequalification Questionnaire'
            : 'Risk Assessment'
        }`,
        {
          type: 'warning',
        }
      )
      return
    }

    const newSectionIndex = formSections.length
    const isFirstSection = newSectionIndex === 0
    const newSection = {
      id: generateUniqueId(),
      title: 'New Section',
      fields: [
        {
          id: generateUniqueId(),
          label: isFirstSection ? 'Vendor Name' : 'Field Label',
          source: isFirstSection
            ? 'name'
            : generateFieldSource(newSectionIndex, 0),
          required: isFirstSection, // Make the vendor name field required
          fieldType: isFirstSection
            ? undefined
            : questionnaireType === QuestionnaireType.PRELIMINARY
            ? ('yesno' as const)
            : ('dropdown' as const),
          options:
            !isFirstSection && questionnaireType === QuestionnaireType.RISK
              ? []
              : undefined,
        },
      ],
    }

    setFormSections([...formSections, newSection])

    // Initialise validation for the new section title
    updateFieldValidation(`section-${newSectionIndex}-title`, {
      error: false,
      helperText: '',
      showHelper: false,
    })

    // Initialise validation for the new field
    updateFieldValidation(`${newSectionIndex}-0-label`, {
      error: false,
      helperText: '',
      showHelper: false,
    })
  }

  // Delete a section
  const deleteSection = (sectionIndex: number) => {
    // Check if this section contains a field with source "name"
    const hasNameSourceField = formSections[sectionIndex].fields.some(
      (field) => field.source === 'name'
    )

    if (hasNameSourceField) {
      // Check if there's another field with source "name" in other sections
      const hasAnotherNameField = formSections.some(
        (section, idx) =>
          idx !== sectionIndex &&
          section.fields.some((field) => field.source === 'name')
      )

      if (!hasAnotherNameField) {
        notify(
          'Cannot delete this section as it contains the only field with source "name". The form must have a field with source "name" to link to the tag category.',
          { type: 'warning' }
        )
        return
      }
    }

    // Remove validations for this section
    const newValidations = { ...fieldValidations }

    // Remove section title validation
    delete newValidations[`section-${sectionIndex}-title`]

    // Remove field validations
    Object.keys(newValidations).forEach((key) => {
      if (key.startsWith(`${sectionIndex}-`)) {
        delete newValidations[key]
      }
    })

    // Update section indices for validations after the deleted section
    Object.keys(newValidations).forEach((key) => {
      if (key.startsWith('section-')) {
        const parts = key.split('-')
        const currentIndex = parseInt(parts[1], 10)
        if (currentIndex > sectionIndex) {
          const newKey = `section-${currentIndex - 1}-${parts[2]}`
          newValidations[newKey] = newValidations[key]
          delete newValidations[key]
        }
      } else {
        const parts = key.split('-')
        const currentIndex = parseInt(parts[0], 10)
        if (currentIndex > sectionIndex) {
          const newKey = `${currentIndex - 1}-${parts[1]}-${parts[2]}`
          newValidations[newKey] = newValidations[key]
          delete newValidations[key]
        }
      }
    })

    setFieldValidations(newValidations)

    setFormSections(formSections.filter((_, index) => index !== sectionIndex))
  }

  // Update a section title
  const updateSectionTitle = (sectionIndex: number, title: string) => {
    const updatedSections = [...formSections]
    updatedSections[sectionIndex].title = title
    setFormSections(updatedSections)

    // Validate the section title
    const validation = validateSectionTitle(sectionIndex, title)
    updateFieldValidation(`section-${sectionIndex}-title`, validation)
  }

  // Add a new field to a section
  const addField = (sectionIndex: number) => {
    // Check if existing fields in this section are valid
    if (!areSectionFieldsValid(sectionIndex)) {
      notify('Please fix validation errors before adding a new field', {
        type: 'warning',
      })
      return
    }

    // Check if maximum questions limit has been reached
    if (questionnaireType && hasReachedMaxQuestions()) {
      notify(
        `Maximum number of questions reached for ${
          questionnaireType === QuestionnaireType.PRELIMINARY
            ? 'Prequalification Questionnaire'
            : 'Risk Assessment'
        }`,
        {
          type: 'warning',
        }
      )
      return
    }

    const updatedSections = [...formSections]
    const newFieldIndex = updatedSections[sectionIndex].fields.length

    // Generate a unique source for the new field
    const newFieldSource = generateFieldSource(sectionIndex, newFieldIndex)

    // For Prequalification Questionnaires, add a Yes/No field
    // For Risk Assessments, add a dropdown field with empty options
    updatedSections[sectionIndex].fields.push({
      id: generateUniqueId(),
      label: 'Field Label',
      source: newFieldSource,
      required: false,
      fieldType:
        questionnaireType === QuestionnaireType.PRELIMINARY
          ? ('yesno' as const)
          : ('dropdown' as const),
      options: questionnaireType === QuestionnaireType.RISK ? [] : undefined,
    })

    setFormSections(updatedSections)

    // Initialize validation for the new field
    updateFieldValidation(`${sectionIndex}-${newFieldIndex}-label`, {
      error: false,
      helperText: '',
      showHelper: false,
    })
  }

  // Delete a field from a section
  const deleteField = (sectionIndex: number, fieldIndex: number) => {
    // Check if this field has source "name"
    const field = formSections[sectionIndex].fields[fieldIndex]
    if (field.source === 'name') {
      // Check if there's another field with source "name"
      const hasAnotherNameField = formSections.some((section, sIdx) =>
        section.fields.some(
          (f, fIdx) =>
            !(sIdx === sectionIndex && fIdx === fieldIndex) &&
            f.source === 'name'
        )
      )

      if (!hasAnotherNameField) {
        notify(
          `Cannot delete this field as it is the only field with source "name". The ${formType.toLowerCase()} must have a field with source "name" to link to the tag category.`,
          { type: 'warning' }
        )
        return
      }
    }

    // Remove validation for this field
    const newValidations = { ...fieldValidations }
    delete newValidations[`${sectionIndex}-${fieldIndex}-label`]

    // Update validations for fields that come after this one
    Object.keys(newValidations).forEach((key) => {
      const parts = key.split('-')
      if (
        parts[0] === sectionIndex.toString() &&
        parseInt(parts[1], 10) > fieldIndex
      ) {
        const newKey = `${parts[0]}-${parseInt(parts[1], 10) - 1}-${parts[2]}`
        newValidations[newKey] = newValidations[key]
        delete newValidations[key]
      }
    })

    setFieldValidations(newValidations)

    const updatedSections = [...formSections]
    updatedSections[sectionIndex].fields = updatedSections[
      sectionIndex
    ].fields.filter((_, index) => index !== fieldIndex)

    // Regenerate sources for all fields in this section to maintain consistency
    updatedSections[sectionIndex].fields.forEach((fieldItem, idx) => {
      fieldItem.source = generateFieldSource(sectionIndex, idx)
    })

    setFormSections(updatedSections)
  }

  // Update a field
  const updateField = (
    sectionIndex: number,
    fieldIndex: number,
    field: Partial<FormField>
  ) => {
    const currentField = formSections[sectionIndex].fields[fieldIndex]

    // Check if this field has source "name" and the user is trying to change it
    if (
      currentField.source === 'name' &&
      field.source !== undefined &&
      field.source !== 'name'
    ) {
      // Check if there's another field with source "name"
      const hasAnotherNameField = formSections.some((section, sIdx) =>
        section.fields.some(
          (f, fIdx) =>
            !(sIdx === sectionIndex && fIdx === fieldIndex) &&
            f.source === 'name'
        )
      )

      if (!hasAnotherNameField) {
        notify(
          `Cannot change the source of this field as it is the only field with source "name". The ${formType.toLowerCase()} must have a field with source "name" to link to the tag category.`,
          { type: 'warning' }
        )
        // Keep the source as "name"
        field.source = 'name'
      }
    }

    // Check if the user is trying to change the label of a field with source "name"
    if (
      currentField.source === 'name' &&
      field.label !== undefined &&
      !field.label.toLowerCase().includes('name')
    ) {
      notify(
        'Fields with source "name" should have "name" in their label for clarity. Adding "Name" to the label.',
        { type: 'info' }
      )
      // Add "Name" to the label
      field.label += ' Name'
    }

    const updatedSections = [...formSections]
    const updatedField = {
      ...updatedSections[sectionIndex].fields[fieldIndex],
      ...field,
    }

    // If label is being updated, validate it
    if (field.label !== undefined) {
      const validation = validateFieldLabel(
        sectionIndex,
        fieldIndex,
        field.label
      )
      updateFieldValidation(`${sectionIndex}-${fieldIndex}-label`, validation)
    }

    // Only auto-generate the source if it's not already set to "name"
    if (updatedField.source !== 'name') {
      updatedField.source = generateFieldSource(sectionIndex, fieldIndex)
    }

    updatedSections[sectionIndex].fields[fieldIndex] = updatedField
    setFormSections(updatedSections)
  }

  if (loading) {
    return <Typography>Loading...</Typography>
  }

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', overflowX: 'visible' }}>
      {/* Form Name and Description */}
      <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Form Details
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Form Name"
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              required
              error={!formName.trim()}
              helperText={!formName.trim() ? 'Form name is required' : ''}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Form Description"
              value={formDescription}
              onChange={(e) => setFormDescription(e.target.value)}
              fullWidth
              variant="outlined"
              margin="normal"
              multiline
              rows={3}
              placeholder="Enter a description for this form"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="form-preview-dialog-title"
      >
        <DialogTitle id="form-preview-dialog-title">Form Preview</DialogTitle>
        <DialogContent>
          {formSections.length > 0 ? (
            <FormPreview
              formSections={formSections}
              formName={formName}
              formDescription={formDescription}
            />
          ) : (
            <Typography>No form sections to preview.</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsPreviewOpen(false)} variant="outlined">
            <Typography>Close</Typography>
          </Button>
        </DialogActions>
      </Dialog>

      {formSections.length > 0 && (
        <>
          {formSections.map((section, sectionIndex) => (
            <Accordion key={section.id} defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                >
                  <Typography variant="subtitle1">{section.title}</Typography>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation()
                      deleteSection(sectionIndex)
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box mb={2}>
                  {(() => {
                    const validationKey = `section-${sectionIndex}-title`
                    const validation = fieldValidations[validationKey] || {
                      error: false,
                      helperText: '',
                      showHelper: false,
                    }

                    return (
                      <TextField
                        label="Section Title"
                        value={section.title}
                        onChange={(e) =>
                          updateSectionTitle(sectionIndex, e.target.value)
                        }
                        onFocus={() => {
                          if (validation.showHelper) {
                            updateFieldValidation(validationKey, {
                              ...validation,
                              showHelper: false,
                            })
                          }

                          // Clear default section title when user focuses on it
                          if (section.title === 'New Section') {
                            updateSectionTitle(sectionIndex, '')
                          }
                        }}
                        onBlur={() => {
                          const newValidation = validateSectionTitle(
                            sectionIndex,
                            section.title
                          )
                          updateFieldValidation(validationKey, {
                            ...newValidation,
                            showHelper: true,
                          })
                        }}
                        fullWidth
                        variant="outlined"
                        size="small"
                        error={validation.error && validation.showHelper}
                        helperText={
                          validation.showHelper ? validation.helperText : ''
                        }
                      />
                    )
                  })()}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <Typography variant="subtitle2" gutterBottom>
                  Fields
                </Typography>

                {section.fields.map((field, fieldIndex) => {
                  const validationKey = `${sectionIndex}-${fieldIndex}-label`
                  const validation = fieldValidations[validationKey] || {
                    error: false,
                    helperText: '',
                    showHelper: false,
                  }

                  return (
                    <Paper key={field.id} elevation={1} sx={{ p: 2, mb: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="Field Label"
                            value={field.label}
                            onChange={(e) =>
                              updateField(sectionIndex, fieldIndex, {
                                label: e.target.value,
                              })
                            }
                            onFocus={() => {
                              if (validation.showHelper) {
                                updateFieldValidation(validationKey, {
                                  ...validation,
                                  showHelper: false,
                                })
                              }

                              // Clear default field label when user focuses on it
                              if (field.label === 'Field Label') {
                                updateField(sectionIndex, fieldIndex, {
                                  label: '',
                                })
                              }
                            }}
                            onBlur={() => {
                              const newValidation = validateFieldLabel(
                                sectionIndex,
                                fieldIndex,
                                field.label
                              )
                              updateFieldValidation(validationKey, {
                                ...newValidation,
                                showHelper: true,
                              })
                            }}
                            fullWidth
                            variant="outlined"
                            size="small"
                            error={validation.error && validation.showHelper}
                            helperText={
                              validation.showHelper ? validation.helperText : ''
                            }
                          />
                          {/* Field source is auto-generated and hidden */}
                          <input type="hidden" value={field.source} />
                        </Grid>

                        {/* Field type specific options */}
                        <Grid item xs={12}>
                          {questionnaireType ===
                          QuestionnaireType.PRELIMINARY ? (
                            <Typography variant="body2" color="textSecondary">
                              This field will have Yes/No answer options.
                            </Typography>
                          ) : (
                            questionnaireType === QuestionnaireType.RISK && (
                              <>
                                <Typography variant="body2" gutterBottom>
                                  Answer Options:
                                </Typography>
                                {!field.options ||
                                field.options.length === 0 ? (
                                  <Box sx={{ mb: 1 }}>
                                    <Typography
                                      variant="body2"
                                      color="textSecondary"
                                    >
                                      No options added yet. Add some options
                                      below.
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Box sx={{ mb: 1 }}>
                                    {field.options.map(
                                      (option, optionIndex) => (
                                        <Box
                                          key={optionIndex}
                                          display="flex"
                                          alignItems="center"
                                          mb={1}
                                        >
                                          <TextField
                                            size="small"
                                            value={option}
                                            onChange={(e) => {
                                              const newOptions = [
                                                ...(field.options || []),
                                              ]
                                              newOptions[optionIndex] =
                                                e.target.value
                                              updateField(
                                                sectionIndex,
                                                fieldIndex,
                                                {
                                                  options: newOptions,
                                                }
                                              )
                                            }}
                                            fullWidth
                                            variant="outlined"
                                          />
                                          <IconButton
                                            size="small"
                                            onClick={() => {
                                              const newOptions = [
                                                ...(field.options || []),
                                              ]
                                              newOptions.splice(optionIndex, 1)
                                              updateField(
                                                sectionIndex,
                                                fieldIndex,
                                                {
                                                  options: newOptions,
                                                }
                                              )
                                            }}
                                          >
                                            <DeleteIcon />
                                          </IconButton>
                                        </Box>
                                      )
                                    )}
                                  </Box>
                                )}
                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => {
                                    const newOptions = [
                                      ...(field.options || []),
                                      '',
                                    ]
                                    updateField(sectionIndex, fieldIndex, {
                                      options: newOptions,
                                    })
                                  }}
                                >
                                  <Typography>Add Option</Typography>
                                </Button>
                              </>
                            )
                          )}
                        </Grid>

                        <Grid item xs={6} sm={6}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.required}
                                onChange={(e) =>
                                  updateField(sectionIndex, fieldIndex, {
                                    required: e.target.checked,
                                  })
                                }
                              />
                            }
                            label="Required"
                            sx={{ mr: 0 }}
                          />
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          sm={6}
                          display="flex"
                          justifyContent="flex-end"
                        >
                          <IconButton
                            size="small"
                            onClick={() =>
                              deleteField(sectionIndex, fieldIndex)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  )
                })}

                <Button
                  onClick={() => addField(sectionIndex)}
                  startIcon={<AddIcon />}
                  disabled={!areSectionFieldsValid(sectionIndex)}
                >
                  <Typography>Add Field</Typography>
                </Button>
              </AccordionDetails>
            </Accordion>
          ))}
        </>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
      >
        <Button
          onClick={addSection}
          startIcon={<AddIcon />}
          variant="outlined"
          disabled={formSections.length > 0 && !isFormValid()}
        >
          <Typography>
            {formSections.length === 0 ? 'Create First Section' : 'Add Section'}
          </Typography>
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {isModal && (
            <Button onClick={onClose} variant="outlined" sx={{ mr: 1 }}>
              <Typography>Cancel</Typography>
            </Button>
          )}
          <Button
            onClick={() => setIsPreviewOpen(true)}
            startIcon={<VisibilityIcon />}
            variant="outlined"
            sx={{ mr: 1 }}
            disabled={!isFormValid()}
          >
            <Typography>Preview</Typography>
          </Button>
          <Button
            onClick={saveVendorForm}
            startIcon={
              saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            variant="contained"
            color="primary"
            disabled={!isFormValid() || saving}
          >
            <Typography>{saving ? 'Saving...' : 'Save'}</Typography>
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

export default OrganisationVendorForm
